import { ArrowLeft } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';

/**
 * The top of every page: leading icon, title, optional subtitle, right-aligned actions,
 * and an optional filter row beneath.
 *
 * The icon is muted while the title is full contrast — that is most of what makes the
 * chrome recede. Wrap each filter control in `FilterField` so the labels line up and the
 * heights agree.
 */
export type PageHeaderProps = {
  icon?: LucideIcon;
  title: string;
  /**
   * ⚠️ [D4.3] THE PARENT LIST, NAMED — NEVER `history.back()`.
   *
   * A page reached from a list needs a way back to that list, and "back" is not the same
   * question as "the previous thing in this tab's history". Arriving from a pasted URL, a
   * notification or a different screen makes `navigate(-1)` land somewhere the operator did
   * not come from, and a control that is sometimes wrong is worse than none — so this names
   * a DESTINATION, which is correct however the page was reached.
   *
   * It renders a real `<a>`, for `NameCell`'s stated reason: a button cannot be ⌘-clicked
   * into a new tab, cannot be copied as a link, and shows no destination in the status bar.
   *
   * ⚠️ IT IS NOT A REPLACEMENT FOR THE SIDEBAR, and it does not try to be a breadcrumb —
   * one hop, to the list this record belongs to.
   */
  parent?: { label: string; to: string };
  subtitle?: ReactNode;
  /** The right cluster. Primary action last. */
  actions?: ReactNode;
  /** One line of controls beneath the title. */
  filters?: ReactNode;
  className?: string;
};

export const PageHeader = ({ icon: Icon, title, parent, subtitle, actions, filters, className }: PageHeaderProps) => (
  <div className={cn('flex flex-col gap-4 border-b border-border pb-4', className)}>
    {/*
      ⚠️ ABOVE THE TITLE ROW, NOT INSIDE IT. That row is `items-baseline` + `flex-wrap` so a
      short subtitle sits optically beside the title ([P3b.2]) and a long one wraps rather
      than truncating ([P4-UX]). A third baseline participant re-opens both defects.
    */}
    {parent && (
      <Link
        to={parent.to}
        data-testid="page-parent"
        className="-mb-2 inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden />
        {parent.label}
      </Link>
    )}
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-2.5">
        {Icon && <Icon className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden />}
        {/*
          ⚠️ [P3b.2] THE SUBTITLE SITS BESIDE THE TITLE, NOT BENEATH IT, when it is short.
          A reference like `PA-2026-0001` is an identifier rather than a sentence: on its own
          line it spent a whole row of a screen whose point is that the form fits, and it
          reads as a caption for something it is actually a PART of. `items-baseline` is what
          makes the two sit on one optical line at different sizes; `flex-wrap` is what lets
          a genuinely long subtitle drop to its own line rather than squeezing the title.
        */}
        {/*
          ⚠️ [amended P4-UX] THE SUBTITLE WRAPS; IT DOES NOT TRUNCATE.

          `truncate` is `white-space: nowrap` plus an ellipsis, which DEFEATED the
          `flex-wrap` above it: the subtitle dropped to its own line and then clipped there
          anyway, so "Command roles are not post types — they live …" ended mid-sentence
          with no tooltip and no way to read the rest. Nobody resizes a browser to read a
          subtitle. `min-w-0` is what lets it shrink to its line before wrapping inside it.

          The `<h1>` keeps `truncate` — a title is one short phrase and a second line of it
          would push every page's content down — and gains a native `title`, which is
          correct here and only here because `title` is typed `string`. `subtitle` is a
          `ReactNode`, so it can never take that attribute; wrapping is its answer.
        */}
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          {/* [C1h §2c] `text-heading` is `foreground` in `standard`, so this renders identically there. */}
          <h1 className="truncate text-xl font-semibold tracking-tight text-heading" title={title}>
            {title}
          </h1>
          {subtitle && <p className="min-w-0 text-sm text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
    {filters && <div className="flex flex-wrap items-end gap-3">{filters}</div>}
  </div>
);
